import type { CredentialEvaluation } from '@dosfilos/domain';
import type { CredentialProgress } from '../../../lib/api/credential-progress.api';
import type { PortalSectionsResult } from '../../../lib/api/enrollment.api';

// ─── El inicio del portal, sin pantalla vacía ───────────────────────────────
// Qué bloques muestra el inicio y con qué datos. Todo sale de lecturas que el
// portal ya hace (y que la vista «Ver portal» ya admite): el expediente
// (`getCredentialProgress`, solo con `graduation_audit`), las matrículas a
// programa y las secciones del portal. Aquí no se calcula nada nuevo: el estado
// del alumno llega del servidor con la regla de la página Alumnos, y el avance,
// con el mismo cálculo que la pestaña de la credencial.

export type HomeBlock = 'welcome' | 'summary' | 'nextStep' | 'courses' | 'empty' | 'contact';

/**
 * El orden de los bloques. Sin `graduation_audit` el inicio es el de siempre
 * —los cursos o el aviso de «sin cursos»— con la bienvenida arriba y el
 * contacto abajo. Con el flag, el resumen del expediente va arriba de los
 * cursos, y sin cursos el inicio no se queda vacío: el resumen ocupa su lugar.
 */
export function homeLayout(input: {
  credentialRecord: boolean;
  /** Tiene secciones (el criterio de siempre para mostrar el bloque de cursos). */
  hasCourses: boolean;
  /** Tiene alguna que el inicio MUESTRA: en marcha, por empezar o recién
   *  terminada. Las archivadas viven en Historial; con solo esas, el bloque
   *  de cursos sería una lista vacía. */
  hasVisibleCourses: boolean;
  hasNextStep: boolean;
  hasContact: boolean;
}): HomeBlock[] {
  const blocks: HomeBlock[] = ['welcome'];
  if (input.credentialRecord) {
    blocks.push('summary');
    if (input.hasNextStep) blocks.push('nextStep');
    if (input.hasVisibleCourses) blocks.push('courses');
  } else {
    blocks.push(input.hasCourses ? 'courses' : 'empty');
  }
  if (input.hasContact) blocks.push('contact');
  return blocks;
}

/** Lo que hace falta de una matrícula a programa para elegir la credencial. */
export interface ProgramEnrollmentLite {
  programId: string;
  status: string;
  /** `YYYY-MM-DD` o ISO; ordena «la más reciente». */
  startedAt?: string | null;
}

/** El programa del alumno: el de su matrícula ACTIVA; si no tiene activa, el de
 *  la más reciente. Null si no tiene ninguna. */
export function currentProgramOf(enrollments: readonly ProgramEnrollmentLite[]): string | null {
  const active = enrollments.filter((e) => e.status === 'active');
  const pool = active.length > 0 ? active : enrollments;
  const latest = [...pool].sort((a, b) => (b.startedAt ?? '').localeCompare(a.startedAt ?? ''))[0];
  return latest?.programId ?? null;
}

const mostUnits = (credentials: readonly CredentialEvaluation[]): CredentialEvaluation | null => {
  let best: CredentialEvaluation | null = null;
  for (const c of credentials) if (!best || c.unitsRequired > best.unitsRequired) best = c;
  return best;
};

/**
 * La credencial «principal»: la del programa en que el alumno está matriculado
 * (la matrícula activa; si no hay activa, la más reciente). Sin matrícula a
 * programa —o si ninguna regla es de su programa—, la que exige más créditos.
 */
export function mainCredential(
  credentials: readonly CredentialEvaluation[],
  programEnrollments: readonly ProgramEnrollmentLite[] = [],
): CredentialEvaluation | null {
  const program = currentProgramOf(programEnrollments);
  if (program) {
    const own = credentials.filter((c) => c.programId === program);
    if (own.length > 0) return mostUnits(own);
  }
  return mostUnits(credentials);
}

export interface RecordSummary {
  unitsEarned: number;
  /** `null` = el seminario no tiene reglas de credencial. */
  unitsRequired: number | null;
  coursesPassed: number;
  gpa: number | null;
  lastTermName: string | null;
  /** El período más reciente SIGUE EN CURSO: se rotula «Período actual», no
   *  «Último período» (que suena a algo que terminó). */
  lastTermIsCurrent: boolean;
  credential: { name: string; percent: number } | null;
}

/** El período en curso HOY, si lo hay: el activo del portal (abierto o en
 *  curso, lo decide el servidor) cuando hoy cae entre su inicio y su fin. */
export function currentTermIdOf(
  data: PortalSectionsResult | undefined,
  today: string,
): string | null {
  const t = data?.activeTerm;
  if (!t) return null;
  return t.startDate <= today && today <= t.endDate ? t.id : null;
}

/** Las cifras del resumen. `progress` ya pasado por `progressFor(…, 'student')`. */
export function recordSummary(
  progress: CredentialProgress,
  programEnrollments: readonly ProgramEnrollmentLite[] = [],
  currentTermId: string | null = null,
): RecordSummary {
  const main = mainCredential(progress.credentials, programEnrollments);
  const terms = [...progress.rows, ...progress.inProgress].sort((a, b) =>
    b.termStartDate.localeCompare(a.termStartDate),
  );
  return {
    unitsEarned: main ? main.unitsEarned : progress.totals.unitsEarned,
    unitsRequired: main ? main.unitsRequired : null,
    coursesPassed: progress.totals.coursesPassed,
    gpa: progress.totals.gpa,
    lastTermName: terms[0]?.termName ?? null,
    lastTermIsCurrent: !!currentTermId && terms[0]?.termId === currentTermId,
    credential: main ? { name: main.name, percent: percentOf(main) } : null,
  };
}

function percentOf(c: CredentialEvaluation): number {
  if (c.eligible) return 100;
  if (c.unitsRequired <= 0) return 0;
  return Math.min(100, Math.round((c.unitsEarned / c.unitsRequired) * 100));
}

/** La inscripción está abierta: la MISMA condición con que la página Inscripción
 *  decide si deja inscribirse. */
export function enrollmentOpenOf(data: PortalSectionsResult | undefined): boolean {
  const term = data?.activeTerm;
  return !!term && term.enrollmentOpen !== false;
}

export type NextStep =
  | { kind: 'graduated'; on: string | null }
  | {
      kind: 'missing';
      courses: number;
      credential: string;
      /** De esos, cuántos está cursando ahora (aún sin nota final). */
      inProgress: number;
    }
  | { kind: 'inProgress'; courses: number }
  | { kind: 'enroll'; termName: string };

/** «Qué sigue». Vacío si no hay nada que decir. */
export function nextSteps(input: {
  progress: CredentialProgress;
  enrollment: PortalSectionsResult | undefined;
  programEnrollments?: readonly ProgramEnrollmentLite[];
}): NextStep[] {
  const { progress } = input;
  const steps: NextStep[] = [];
  if (progress.studentStatus === 'graduated') {
    // La graduación más reciente que el registro tiene con fecha.
    const dates = progress.recordedGraduations
      .map((g) => g.endedAt)
      .filter((d): d is string => !!d)
      .sort();
    steps.push({ kind: 'graduated', on: dates[dates.length - 1] ?? null });
  } else {
    // Lo que está en curso todavía no cuenta para la credencial (no tiene nota
    // final): por eso «te faltan» lo incluye, y se dice cuántos de ellos ya
    // está cursando. Sin credencial que medir, se dice solo lo que cursa.
    const inProgress = progress.inProgress.length;
    const main = mainCredential(progress.credentials, input.programEnrollments ?? []);
    if (main && !main.eligible && main.coursesMissing > 0) {
      steps.push({
        kind: 'missing',
        courses: main.coursesMissing,
        credential: main.name,
        inProgress,
      });
    } else if (inProgress > 0) {
      steps.push({ kind: 'inProgress', courses: inProgress });
    }
  }
  if (enrollmentOpenOf(input.enrollment)) {
    steps.push({ kind: 'enroll', termName: input.enrollment!.activeTerm!.name });
  }
  return steps;
}

/** El nombre del saludo: el del alumno; en «Ver portal», su código. */
export function greetingName(input: {
  viewingCode: string | null | undefined;
  viewing: boolean;
  firstName: string | null;
}): string | null {
  if (input.viewing) return input.viewingCode ?? null;
  return input.firstName?.trim() || null;
}

export interface PortalContact {
  address: string | null;
  phone: string | null;
  mapsUrl: string | null;
}

/** Datos de contacto que se muestran: solo los que el seminario completó. */
export function contactOf(config: Record<string, unknown> | undefined): PortalContact | null {
  const landing = (config?.['landing'] ?? null) as { contact?: unknown } | null;
  const c = (landing?.contact ?? null) as Record<string, unknown> | null;
  if (!c) return null;
  const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const address = [str(c['address']), str(c['city'])].filter(Boolean).join(', ') || null;
  const phone = str(c['phone']);
  const mapsUrl = str(c['mapsUrl']);
  return address || phone ? { address, phone, mapsUrl } : null;
}

const PROGRAM_ORDER: Record<string, number> = {
  active: 0,
  on_leave: 1,
  graduated: 2,
  withdrawn: 3,
};

/** Los programas del alumno para la bienvenida: primero el vigente, sin repetir. */
export function programNamesOf(
  enrollments: ReadonlyArray<{ programId: string; status: string }>,
  names: ReadonlyMap<string, string>,
): string[] {
  const sorted = [...enrollments].sort(
    (a, b) => (PROGRAM_ORDER[a.status] ?? 9) - (PROGRAM_ORDER[b.status] ?? 9),
  );
  return [...new Set(sorted.map((e) => names.get(e.programId)).filter((n): n is string => !!n))];
}

/** Los precios de Inscripción: solo con aranceles configurados, y SIEMPRE en
 *  la moneda del programa. Sin ellos, ni un «0» ni una moneda supuesta. Un
 *  servidor anterior que no dice `pricing` se juzga por la moneda. */
export function enrollPricingOf(
  data: PortalSectionsResult | undefined,
): { currency: string } | null {
  if (!data?.currency || data.pricing === false) return null;
  return { currency: data.currency };
}
